import ConfirmModal from '../ConfirmModal/ConfirmModal';
import { useConfirmDataUnitModal } from './useConfirmDataUnitModal';
import {
  activeCompanySelector,
  queryKeySelector,
} from '@/core/store/user/userSlice';
import { useAppSelector } from '@/hooks';
import { confirmDataUnit, getDataUnits } from '@/core/api';
import { useQueryClient } from '@tanstack/react-query';
import { formatDate } from '@/core/helpers';
import { useTranslation } from 'react-i18next';
import { PropsWithChildren } from 'react';
import useNotification from '@/hooks/useNotification';
import { normalizeDateFE } from '@/core/normalization';

export function ConfirmDataUnitModal({ children }: PropsWithChildren) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();
  const { activeDU, activeDUStats, isModalOpened, closeModal, activeDUDate } =
    useConfirmDataUnitModal();
  const activeCompany = useAppSelector(activeCompanySelector);
  const queryKey = useAppSelector(queryKeySelector);

  return (
    <ConfirmModal
      name='ConfirmDataUnit'
      confirmBtnText='Yes, I confirm'
      isModalOpened={isModalOpened}
      closeModal={closeModal}
      confirm={(setIsConfirm) => {
        if (!activeDU) return;
        if (!activeCompany) return;
        setIsConfirm(true);
        confirmDataUnit(activeCompany.company_id, activeDU.id)
          .then(() => getDataUnits(activeCompany?.company_id!))
          .then((DURes) => {
            queryClient.setQueryData(['SellerDataUnits', queryKey], DURes);
            setIsConfirm(false);
            showNotification({
              type: 'success',
              text: t('Data for *period* has been confirmed').replace(
                '*period*',
                formatDate(normalizeDateFE(activeDU.date), 'MMMM yyyy')
              ),
            });
            closeModal();
          })
          .catch(() => {
            setIsConfirm(false);
          });
      }}
      title='Confirm that data is complete and correct'
    >
      <div className='space-y-2'>
        {activeDUDate && (
          <div>
            <div className='mb-3 text-h4 font-extrabold'>
              {t('Confirm your *date* data').replace(
                '*date*',
                formatDate(activeDUDate, 'MMMM yyyy')
              )}
            </div>
            {children}
          </div>
        )}
        <div>
          {t(
            'Make sure all your data is correct and complete before confirming. After confirmation, we will start preparing your VAT filing.'
          )}
        </div>
        <div>
          <span className='font-semibold'>{t('Review carefully')}:</span>
          &nbsp;
          <span>
            {t('Correct any errors or upload missing invoices if needed.')}
          </span>
        </div>
        <div>
          <ul className='list-disc pl-6'>
            {activeDUStats && activeDUStats.warnings_count > 0 && (
              <li>
                {activeDUStats.warnings_count}{' '}
                <span>
                  {t('invoices have notices but will be included to reports')}
                </span>
              </li>
            )}
            {activeDUStats && activeDUStats.errors_count > 0 && (
              <li className='font-semibold text-red-500'>
                {activeDUStats.errors_count}{' '}
                <span>
                  {t(
                    'invoices have errors and will not be included to reports'
                  )}
                </span>
              </li>
            )}
          </ul>
        </div>
        <div className='pb-3'>
          {t(
            'Any corrections due to incorrect data or late confirmation may result in additional charges and penalties. Please confirm ASAP to keep the process on track!'
          )}
        </div>
        <div className='!mb-8 rounded-md bg-gray-400 px-3 py-2'>
          <span className='font-semibold'>{t('Important')}:</span>&nbsp;
          <span>
            {t(
              'Once you confirm, you will not be able to upload or correct any data. The data for this month will be locked and cannot be edited after confirmation'
            )}
          </span>
        </div>
      </div>
    </ConfirmModal>
  );
}
